import { useEffect, useRef, useState } from "react";
import { Lock, X } from "lucide-react";
import { useT } from "@/i18n";
import { Button } from "@/components/ui";
import { NequiBadge } from "@/components/NequiBrand";
import { cn } from "@/lib/cn";

type Props = {
  open: boolean;
  defaultPhone: string;
  amountCop: number;
  loading: boolean;
  errorCode?: string | null;
  errorMessage?: string | null;
  onCancel: () => void;
  onConfirm: (phone: string) => void;
};

const formatCop = (cop: number) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(cop);

/** Strip the +57 prefix if present so the input shows just the 10 digits. */
function toLocal(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("57")) return digits.slice(2);
  if (digits.length === 13 && digits.startsWith("057")) return digits.slice(3);
  return digits.slice(-10);
}

export function NequiPhoneDialog({
  open,
  defaultPhone,
  amountCop,
  loading,
  errorCode,
  errorMessage,
  onCancel,
  onConfirm,
}: Props) {
  const { t } = useT();
  const [phone, setPhone] = useState(() => toLocal(defaultPhone));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setPhone(toLocal(defaultPhone));
      // Tiny delay so the autoFocus lands after the open animation.
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, defaultPhone]);

  if (!open) return null;

  const isNotFound = errorCode === "NEQUI_PAYER_NOT_FOUND";
  const valid = /^\d{10}$/.test(phone);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 animate-fade-in" onClick={loading ? undefined : onCancel} />
      <div
        className="relative w-full max-w-md overflow-hidden rounded-t-3xl bg-white shadow-pop animate-scale-in sm:rounded-3xl sm:m-4"
      >
        <div
          className="absolute inset-x-0 top-0 h-1.5"
          style={{ background: "linear-gradient(90deg, #E0007F, #FF1493 50%, #E0007F)" }}
          aria-hidden
        />

        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="btn-ghost absolute right-4 top-4 h-8 w-8 p-0"
          aria-label={t("common.close")}
        >
          <X className="h-4 w-4" />
        </button>

        <div className="px-6 pt-10 pb-6 sm:px-8">
          <NequiBadge />
          <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-900">
            {t("nequi.dialog_title")}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            {t("nequi.dialog_subtitle", { amount: formatCop(amountCop) })}
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!valid || loading) return;
              onConfirm(phone);
            }}
            className="mt-6 space-y-4"
          >
            <label className="block">
              <span className="label">{t("nequi.phone_label")}</span>
              <div
                className={cn(
                  "mt-1.5 flex items-center overflow-hidden rounded-xl border bg-white transition focus-within:ring-2",
                  isNotFound
                    ? "border-red-300 focus-within:ring-red-200"
                    : "border-slate-200 focus-within:border-brand-500 focus-within:ring-brand-500/20"
                )}
              >
                <span className="border-r border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-500">
                  🇨🇴 +57
                </span>
                <input
                  ref={inputRef}
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  maxLength={10}
                  className="flex-1 border-0 bg-white px-3 py-2.5 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
                  placeholder="3001234567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  disabled={loading}
                />
              </div>
              <p className="mt-1.5 text-xs text-slate-500">{t("nequi.phone_helper")}</p>
            </label>

            {errorCode && errorMessage && (
              <div
                className={cn(
                  "rounded-xl border px-3 py-2 text-sm",
                  isNotFound
                    ? "border-red-200 bg-red-50 text-red-700"
                    : "border-amber-200 bg-amber-50 text-amber-800"
                )}
              >
                {isNotFound ? t("nequi.err_not_found") : errorMessage}
              </div>
            )}

            <Button type="submit" size="lg" block loading={loading} disabled={!valid}>
              <Lock className="h-4 w-4" />
              {t("nequi.confirm")}
            </Button>

            <p className="text-center text-xs text-slate-500">
              {t("nequi.dialog_disclaimer")}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
